import { AccessToken } from '@/entities/AccessToken'
import { createUnknownErrorResult } from '@/entities/FetchResult'
import { FetchResult, FetchResultType } from '@/entities/FetchResult'
import { AuthenticatedUser, UserProfileV3Resource } from '@/entities/User/User'
import { transformToAuthenticatedUser } from '@/entities/User/UserTransformers'
import { authorizedFetch } from '@/helpers/authorizedFetch'
import { reportError, reportMessage } from '@/helpers/errorReporting'
import { getTenant } from '@/helpers/tenant'
import { generateApiUrl } from '@/helpers/url'

export const authenticateEmployeeApiCall = async (employeeNumber: number, pincode: string): Promise<FetchResult<AccessToken, string>> => {
    try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/v2/auth/token/employee`, {
            body: JSON.stringify({ employee_number: employeeNumber, pincode }),
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                TENANT: getTenant(),
            },
        })

        if (! response.ok) {
            reportMessage('authenticateEmployeeApiCall', `authenticateEmployeeApiCall failed with status code ${response.status}`)

            return createUnknownErrorResult(response.status)
        }

        return {
            status: 200,
            type: FetchResultType.Success,
            data: await response.json(),
        }
    } catch (error) {
        reportError('authenticateEmployeeApiCall', error)

        return createUnknownErrorResult(500)
    }
}

export const getAuthenticatedUserApiCall = async (): Promise<FetchResult<AuthenticatedUser, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: '/api/v3/users/profile/me',
        })

        const response = await authorizedFetch(url)

        const doc: UserProfileV3Resource | null = await response.json()

        if (! doc) {
            reportMessage('getAuthenticatedUserApiCall', `getAuthenticatedUserApiCall failed with status code ${response.status}`)

            return createUnknownErrorResult(response.status)
        }

        const user = transformToAuthenticatedUser(doc)

        return {
            status: 200,
            type: FetchResultType.Success,
            data: user,
        }
    } catch (error) {
        reportError('getAuthenticatedUserApiCall', error)

        return createUnknownErrorResult(500)
    }
}

export const getMasterCodeApiCall = async (masterCode: string): Promise<FetchResult<boolean, string>> => {
    try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/v3/timer/master-code`, {
            body: JSON.stringify({ masterCode }),
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                TENANT: getTenant(),
            },
        })

        const doc = await response.json()

        if (! doc || doc.code !== 200) {
            reportMessage('getMasterCodeApiCall', `getMasterCodeApiCall failed with status code ${response.status}`)

            return createUnknownErrorResult(response.status)
        }

        return {
            status: 200,
            type: FetchResultType.Success,
            data: true,
        }
    } catch (error) {
        reportError('getMasterCodeApiCall', error)

        return createUnknownErrorResult(500)
    }
}
